// eslint-disable-next-line max-len
import { NotificationsFilterPopupComponent } from '@components/popups/notifications-filter-popup/notifications-filter-popup.component';
import { ChangeLevel, NotificationsSidebarStatus, NotificationType } from '@enums/notifications';
import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	effect,
	inject,
	DestroyRef,
} from '@angular/core';
import { NotificationsService } from '@services/notifications';
import { VolumeNotification } from '@models/notification';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_NOTIFICATIONS_FILTERS } from '@helpers/consts.helper';
import { Option } from 'rustic';
import { LocalStorageManagerService } from '@services/data-managers/local-storage-manager.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-notifications-sidenav',
	templateUrl: './notifications-sidenav.component.html',
	styleUrls: ['./notifications-sidenav.component.less'],
	standalone: true,
	imports: [MatIcon, TranslateModule],
})
export class NotificationsSidenavComponent implements OnInit {
	private readonly _ns = inject(NotificationsService);
	private readonly _md = inject(MatDialog);
	private readonly _lsm = inject(LocalStorageManagerService);
	private readonly destroyRef = inject(DestroyRef);

	sidebarStatus: NotificationsSidebarStatus = NotificationsSidebarStatus.Close;
	selectedType: NotificationType = NotificationType.Volume;

	volumeNotifications: VolumeNotification[] = [];
	filteredVolumeNotifications: VolumeNotification[] = [];

	// eslint-disable-next-line @typescript-eslint/naming-convention
	ChangeLevel: typeof ChangeLevel = ChangeLevel;

	// eslint-disable-next-line @typescript-eslint/naming-convention
	readonly NotificationsSidebarStatus: typeof NotificationsSidebarStatus =
		NotificationsSidebarStatus;

	private _filters: { [key: string]: Option<string | string[] | ChangeLevel> } =
		DEFAULT_NOTIFICATIONS_FILTERS;

	constructor() {
		effect(() => {
			this.sidebarStatus = this._ns.notificationsSidebarStatus();
		});

		effect(() => {
			this.selectedType = this._ns.notificationTypeSelected();
		});

		effect(() => {
			this.volumeNotifications = this._ns.volumeNotifications();
			this.filterNotifications();
		});
	}

	ngOnInit(): void {
		this._ns.loadVolumeNotifications();
	}

	onFiltersClick(): void {
		const ref = this._md.open(NotificationsFilterPopupComponent);

		ref.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this._ns.loadVolumeNotifications();
			});
	}

	filterNotifications(): void {
		this._filters = this._lsm.filtersNotifications;

		let list = this.volumeNotifications;

		const endNameFilter = this._filters.endName as string[];
		const levelFilter = this._filters.level as string[];

		if (endNameFilter.length > 0) {
			list = this.volumeNotifications.filter((n) => {
				const cname = n.currency_name.toLocaleLowerCase();

				return endNameFilter.some((f) => {
					const fLower = f.toLocaleLowerCase();
					const cnameup = `up${fLower}`;
					const cnamedown = `down${fLower}`;

					return (
						cname.endsWith(fLower) &&
						!cname.includes(cnameup) &&
						!cname.includes(cnamedown)
					);
				});
			});
		}

		if (levelFilter.length > 0) {
			const levelList = this.volumeNotifications.filter((n) => {
				const cname = n.currency_name.toLocaleLowerCase();

				return levelFilter.some((f) => cname.includes(`${f.toLocaleLowerCase()}usdt`));
			});

			if (endNameFilter.length > 0) {
				list.push(...levelList);
			} else {
				list = levelList;
			}
		}

		if (this._filters.currency) {
			list = list.filter(
				(c) =>
					c.currency_name
						.toLocaleLowerCase()
						.indexOf((this._filters.currency as string).toLocaleLowerCase()) !== -1,
			);
		}

		const changeLevel = this._filters.change;

		if (changeLevel != null) {
			list = list.filter(
				(c) =>
					(changeLevel === ChangeLevel.Up && c.change > 0) ||
					(changeLevel === ChangeLevel.Down && c.change < 0),
			);
		}

		this.filteredVolumeNotifications = list;
	}
}
